import { memo, useState, useCallback, useEffect, useRef } from "react";
import { MapPin, AlertTriangle, Search, Loader2 } from "lucide-react";
import type { CarCardData } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { AppleMap } from "@/components/maps/AppleMap";

interface SearchMapProps {
  cars: CarCardData[];
  /**
   * Called when the user explicitly clicks the "Search this area" button
   * after panning or zooming the map. Replaces the previous debounced
   * auto-search-on-pan: pin counts no longer drift silently as the user
   * moves the map; instead a button surfaces and the surrounding search
   * only re-runs against the new viewport when the user opts in.
   *
   * Programmatic re-fits driven by a new `cars` prop (initial load,
   * filter change, or auto-zoom-out) are suppressed so the button never
   * appears as a false positive.
   */
  onSearchArea?: (bounds: { north: number; south: number; east: number; west: number }) => void;
  /**
   * Fired when the user pans/zooms the map (i.e. when the "Search this
   * area" button surfaces) but BEFORE they commit the new viewport via
   * that button. Lets the parent clear transient hints like the
   * post-widen "showing the closest N" banner the moment the user
   * starts moving the map again.
   */
  onUserPan?: () => void;
  onSwitchToGrid?: () => void;
  selectedCarId?: number | null;
  onSelectedCarIdChange?: (id: number | null) => void;
  /** Whether the latest search results are still being fetched. */
  resultLoading?: boolean;
  /**
   * When provided and no cars have valid coordinates, centres the map on this
   * location at a city-level zoom. Passed straight through to `AppleMap`.
   */
  locationAnchor?: { lat: number; lng: number } | null;
}

// After the parent supplies a new `cars` prop the AppleMap re-fits via
// showItems(), which produces one or more region-change-end events. AppleMap
// already swallows the first ~2 of those internally; this longer time-window
// guard protects against jankier programmatic moves (e.g. several rapid
// auto-zoom-out hops) being mistaken for user gestures and incorrectly
// surfacing the "Search this area" button.
const PROGRAMMATIC_GRACE_MS = 800;

function SearchMapImpl({
  cars,
  onSearchArea,
  onUserPan,
  onSwitchToGrid,
  selectedCarId: controlledSelectedCarId,
  onSelectedCarIdChange,
  resultLoading = false,
  locationAnchor,
}: SearchMapProps) {
  const { t } = useTranslation();
  const [mapError, setMapError] = useState<string | null>(null);
  const [internalSelectedCarId, setInternalSelectedCarId] = useState<number | null>(null);
  const isSelectionControlled = controlledSelectedCarId !== undefined;
  const selectedCarId = isSelectionControlled ? controlledSelectedCarId : internalSelectedCarId;
  const updateSelectedCarId = useCallback((id: number | null) => {
    if (!isSelectionControlled) setInternalSelectedCarId(id);
    onSelectedCarIdChange?.(id);
  }, [isSelectionControlled, onSelectedCarIdChange]);

  const geoCars = cars.filter(c =>
    c.latitude != null && c.longitude != null &&
    Number.isFinite(c.latitude) && Number.isFinite(c.longitude) &&
    c.latitude >= -90 && c.latitude <= 90 &&
    c.longitude >= -180 && c.longitude <= 180
  );

  // Track when the cars prop changes so we can ignore the bounds-change
  // events the AppleMap fires while it re-fits to those new cars. Without
  // this, the "Search this area" button would surface immediately after
  // every search, before the user has actually moved the map.
  const carsSigRef = useRef<string>("");
  const ignoreUntilRef = useRef<number>(Date.now() + PROGRAMMATIC_GRACE_MS);
  // Bounds the user has panned/zoomed to but not yet committed via the
  // "Search this area" button. When non-null, the button is visible.
  const [pendingBounds, setPendingBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);

  useEffect(() => {
    const sig = cars.map((c) => c.id).join(",");
    if (sig !== carsSigRef.current) {
      carsSigRef.current = sig;
      ignoreUntilRef.current = Date.now() + PROGRAMMATIC_GRACE_MS;
      // Hide any pending "Search this area" button — the cars list just
      // updated, so the map is about to re-fit to a fresh viewport and
      // any previously-pending bounds no longer reflect what the user
      // is looking at.
      setPendingBounds(null);
    }
  }, [cars]);

  const handleBoundsChange = useCallback((bounds: { north: number; south: number; east: number; west: number }) => {
    if (!onSearchArea) return;
    if (Date.now() < ignoreUntilRef.current) return;
    // Belt-and-suspenders NaN guard: AppleMap already guards in region-change-end,
    // but drop any non-finite bounds here too so they never reach setPendingBounds
    // or the parent's search logic.
    if (
      !Number.isFinite(bounds.north) ||
      !Number.isFinite(bounds.south) ||
      !Number.isFinite(bounds.east) ||
      !Number.isFinite(bounds.west)
    ) {
      console.warn("[SearchMap] handleBoundsChange received non-finite bounds — ignoring", bounds);
      return;
    }
    setPendingBounds(bounds);
    // Notify the parent that the user has started moving the map, so
    // any transient hints tied to the previous viewport (e.g. the
    // post-widen "showing the closest N" banner) can clear immediately
    // rather than wait for the user to actually click "Search this area".
    onUserPan?.();
  }, [onSearchArea, onUserPan]);

  const handleSearchAreaClick = useCallback(() => {
    if (!pendingBounds || !onSearchArea) return;
    const bounds = pendingBounds;
    setPendingBounds(null);
    onSearchArea(bounds);
  }, [pendingBounds, onSearchArea]);

  const handleMarkerClick = useCallback((carPath: string, carId?: number) => {
    // First click on a price-pill marker selects the car (turning it into a
    // circular car-photo marker). Clicking the already-selected marker opens
    // the car page.
    if (carId != null && selectedCarId !== carId) {
      updateSelectedCarId(carId);
      return;
    }
    const base = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
    window.open(`${base}${carPath}`, "_blank");
  }, [selectedCarId, updateSelectedCarId]);

  if (mapError) {
    return (
      <div className="relative flex h-full min-h-[400px] w-full items-center justify-center rounded-none border border-border bg-muted/30 p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
          <p className="mt-3 text-sm font-semibold text-foreground">
            {t("search.map_unavailable_title", "Map is temporarily unavailable")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("search.map_unavailable_description", "We couldn't load the map right now. You can keep browsing in grid view.")}
          </p>
          {onSwitchToGrid && (
            <button
              type="button"
              onClick={onSwitchToGrid}
              className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              {t("search.switch_to_grid", "Switch to grid")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <AppleMap
        cars={cars}
        onBoundsChange={handleBoundsChange}
        onMarkerClick={handleMarkerClick}
        onError={setMapError}
        selectedCarId={selectedCarId}
        locationAnchor={locationAnchor}
        height="100%"
        className="h-full rounded-none"
      />

      {pendingBounds && onSearchArea && (
        <button
          type="button"
          onClick={handleSearchAreaClick}
          className="absolute left-1/2 top-[calc(1rem+env(safe-area-inset-top))] z-[1001] flex -translate-x-1/2 items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg transition-opacity hover:opacity-90 md:top-4"
          data-testid="map-search-this-area"
        >
          <Search className="h-4 w-4" />
          {t("common.search_this_area")}
        </button>
      )}

      {geoCars.length === 0 && resultLoading && (
        <div
          className="absolute inset-0 z-[1000] flex items-center justify-center material-panel"
          role="status"
          aria-live="polite"
          aria-label={t("common.searching")}
          data-testid="map-results-loading"
        >
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-muted-foreground">
              {t("common.searching")}
            </p>
          </div>
        </div>
      )}

      {geoCars.length > 0 && resultLoading && (
        <div
          className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top))] z-[1000] flex items-center gap-2 rounded-full border border-border material-panel px-3 py-2 text-sm font-medium text-foreground shadow-lg md:top-4"
          role="status"
          aria-live="polite"
          aria-label={t("common.updating")}
          data-testid="map-results-updating"
        >
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
          {t("common.updating")}
        </div>
      )}

      {geoCars.length === 0 && !resultLoading && (
        <div className="absolute inset-0 z-[1000] flex items-center justify-center material-panel">
          <div className="text-center">
            <MapPin className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="mt-2 text-sm font-medium text-muted-foreground">
              {t("common.no_cars_on_map")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Memoised export (task #7081 — INP): both search pages re-render on every
// hover/filter/params change, and an un-memoised SearchMap re-rendered the
// whole map subtree (markers, overlays, AppleMap wrapper) inside each of
// those interaction tasks. Callers pass stable (useCallback/setState)
// handlers and a memoised `cars` array, so shallow prop equality
// short-circuits everything except genuine result/selection changes.
export const SearchMap = memo(SearchMapImpl);
